import { useMemo, useState, useRef } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import BarChart from "../../../components/graphs/BarChart";
import { useTranslation } from "react-i18next";
import BarGraphLoader from "../../../components/skeletonLoaders/BarGraphLoader";
import useAuthValue from "../../authModule/hooks/useAuthValue";
import useFilterValue from "../hooks/useFilterValue";
import { buildQueryStringFromFilters } from "../../../helpers/buildQueryStringFromFilters";
//@ts-ignore
import { useScreenshot } from "use-react-screenshot";
import useSelectedHospitalValue from "../../hospitalModule/hooks/useSelectedHospitalValue";
import ChartContainer, {
  RenderChartProps,
} from "../../../components/ChartContainer";
import useDashboardExport from "../hooks/useDashboardExport";
import useGetCaseRecoveryHandOffTimeQuery from "../hooks/useGetCaseRecoveryHandoffTimeQuery";
import useGetCaseInductionTimeQuery from "../hooks/useGetCaseInductionTimeQuery";
import useGetAnesthesiaStartIntervalQuery from "../hooks/useGetAnesthesiaStartIntervalQuery";
import useGetAnesthesiaStartToProcedureStartIntervalQuery from "../hooks/useGetAnesthesiaStartToProcedureStartIntervalQuery";
import { ANESTHESIA_START_INTERVAL_RESPONSE, ANESTHESIA_START_TO_PROCEDURE_START_INTERVAL_RESPONSE, CASE_INDUCTION_TIME_RESPONSE, CASE_RECOVERY_HANDOFF_TIME_RESPONSE } from "../types/ResponseTypes";

interface AnaesthesiaAnalysisProps {
  exportRoleBaesdScreenshot?: any;
}

// Table row type for On-Time Start/End Percentage
export type OTOnTimeStartEndRow = {
  otName: string;
  totalCases: number;
  percentOnTimeStarts: string;
  percentOnTimeEnds: string;
};

// Extract data fetching hooks into a custom hook
const useAnesthesiaData = (
  tenantId?: string,
  hospitalId?: string,
  queryString?: string
) => {
  const {
    data: { data: caseRecoveryHandoffTimeData } = {},
    isLoading: isGettingCaseRecoveryHandoffTime,
  } = useGetCaseRecoveryHandOffTimeQuery(tenantId, hospitalId, queryString);

  const { data: { data: caseInductionTimeData } = {}, isLoading: isGettingCaseInductionTime } =
    useGetCaseInductionTimeQuery(tenantId, hospitalId, queryString);

  const {
    data: { data: anesthesiaStartIntervalData } = {},
    isLoading: isGettinganesthesiaStartInterval,
  } = useGetAnesthesiaStartIntervalQuery(tenantId, hospitalId, queryString);

  const {
    data: { data: anesthesiaStartToProcedureStartIntervalData } = {},
    isLoading: isGettingAnesthesiaStartToProcedureStartInterval,
  } = useGetAnesthesiaStartToProcedureStartIntervalQuery(tenantId, hospitalId, queryString);

  return {
    caseRecoveryHandoffTimeData,
    isGettingCaseRecoveryHandoffTime,
    caseInductionTimeData,
    isGettingCaseInductionTime,
    anesthesiaStartIntervalData,
    isGettinganesthesiaStartInterval,
    anesthesiaStartToProcedureStartIntervalData,
    isGettingAnesthesiaStartToProcedureStartInterval,
  };
};

const AnaesthesiaAnalysis: React.FC<AnaesthesiaAnalysisProps> = ({
  exportRoleBaesdScreenshot,
}) => {
  const { t } = useTranslation();

  const currentHospitalDetails = useSelectedHospitalValue();
  const { tenantId: authTenantId, hospitalId: authHospitalId } = useAuthValue();
  const tenantId = currentHospitalDetails?.tenantId ?? authTenantId;
  const hospitalId = currentHospitalDetails?.uuid ?? authHospitalId;
  const screenshotRef = useRef(null);
  const [isExporting, setIsExporting] = useState(false);
  const filters = useFilterValue();
  const queryString = buildQueryStringFromFilters(filters);
  const [, takeScreenshot] = useScreenshot();

  const {
    caseRecoveryHandoffTimeData,
    isGettingCaseRecoveryHandoffTime,
    caseInductionTimeData,
    isGettingCaseInductionTime,
    anesthesiaStartIntervalData,
    isGettinganesthesiaStartInterval,
    anesthesiaStartToProcedureStartIntervalData,
    isGettingAnesthesiaStartToProcedureStartInterval,
  } = useAnesthesiaData(tenantId, hospitalId, queryString);

  useDashboardExport({
    exportRef: exportRoleBaesdScreenshot,
    screenshotRef,
    setIsExporting,
    takeScreenshot,
    filename: "orva-anesthesia-analysis.pdf",
  });

  const anesthesiaStartIntervalChartData = useMemo(() => {
    if (
      !anesthesiaStartIntervalData ||
      !Array.isArray(anesthesiaStartIntervalData)
    )
      return [];
    return anesthesiaStartIntervalData.map((item: ANESTHESIA_START_INTERVAL_RESPONSE) => ({
      label: item.anesthesiologist,
      value: item.averageIntervalMinutes,
    }));
  }, [anesthesiaStartIntervalData]);

  const caseRecoveryHandoffTimeChartData = useMemo(() => {
    if (
      !caseRecoveryHandoffTimeData ||
      !Array.isArray(caseRecoveryHandoffTimeData)
    )
      return [];
    return caseRecoveryHandoffTimeData.map((item: CASE_RECOVERY_HANDOFF_TIME_RESPONSE) => ({
      label: item.anesthesiologist,
      value: item.averageHandoffMinutes,
    }));
  }, [caseRecoveryHandoffTimeData]);

  const caseInductionTimeChartData = useMemo(() => {
    if (
      !caseInductionTimeData ||
      !Array.isArray(caseInductionTimeData)
    )
      return [];
    return caseInductionTimeData.map((item: CASE_INDUCTION_TIME_RESPONSE) => ({
      label: item.anesthesiologist,
      value: item.averageInductionMinutes,
    }));
  }, [caseInductionTimeData]);

  const anesthesiaStartToProcedureStartIntervalChartData = useMemo(() => {
    if (
      !anesthesiaStartToProcedureStartIntervalData ||
      !Array.isArray(anesthesiaStartToProcedureStartIntervalData)
    )
      return [];
    return anesthesiaStartToProcedureStartIntervalData.map((item: ANESTHESIA_START_TO_PROCEDURE_START_INTERVAL_RESPONSE) => ({
      label: item.anesthesiologist,
      value: item.averageIntervalMinutes,
    }));
  }, [anesthesiaStartToProcedureStartIntervalData]);

  const mappedChartArr = [
    {
      chartLabel: "In_Room_Anesthesia_Start_Interval",
      chartData: anesthesiaStartIntervalChartData,
      isLoading: isGettinganesthesiaStartInterval,
      tooltipTitle: "In_Room_Anesthesia_Start_Interval_Tooltip",
    },
    {
      chartLabel: "Induction_Time",
      chartData: caseInductionTimeChartData,
      isLoading: isGettingCaseInductionTime,
      tooltipTitle: "Induction_Time_Tooltip",
    },
    {
      chartLabel: "Recovery_Handoff_Time",
      chartData: caseRecoveryHandoffTimeChartData,
      isLoading: isGettingCaseRecoveryHandoffTime,
      tooltipTitle: "Recovery_Handoff_Time_Tooltip",
    },
    {
      chartLabel: "Anesthesia_Start_to_Procedure_Start_Interval",
      chartData: anesthesiaStartToProcedureStartIntervalChartData,
      isLoading: isGettingAnesthesiaStartToProcedureStartInterval,
      tooltipTitle: "Anesthesia_Start_to_Procedure_Start_Interval_Tooltip",
    },
  ];

  const renderBarChart = ({
    data,
    title,
    tooltipTitle,
    isLoading,
    props,
  }: RenderChartProps) => {
    if (isLoading) {
      return <BarGraphLoader {...props} />;
    }

    return (
      <BarChart
        key={hospitalId}
        title={t(title)}
        tooltipTitle={t(tooltipTitle)}
        data={data}
        fromOTByOT={true}
        {...props}
      />
    );
  };

  return (
    <Box ref={screenshotRef} flex={1} py={2} sx={{ px: isExporting ? 2 : 0 }}>
      <Grid container spacing={1.5}>
        {mappedChartArr.map((item) => (
          <Grid key={item.chartLabel} size={{ xs: 12, md: 6 }}>
            <ChartContainer
              isLoading={item.isLoading}
              data={item.chartData}
              title={item.chartLabel}
              tooltipTitle={item.tooltipTitle}
              props={{
                showLabels: false,
                showXAxis: true,
                categoryFontSize: 12,
                labelColNumber: 2,
                showAverageLine: true,
                showLabelInHeader: false,
              }}
              renderChart={renderBarChart}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default AnaesthesiaAnalysis;
